import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * One rail item (desktop-shell.md §3, superseded per owner's direct
 * correction: icon + visible label text, side by side, left-aligned, full
 * row width — not the icon-only 48x48 hit target with a hover tooltip this
 * component used before. `layout.desktopShellRailWidthExpanded` (208px,
 * already reserved-but-unused in tokens.css for exactly this contingency)
 * is now the live rail width (see `DesktopShell.tsx`), so the hover tooltip
 * this component used to disclose the label is redundant and removed.
 *
 * Active state stays tinted-background-plus-vivid-icon — explicitly NOT a
 * left border (the standing v0.10 anti-pattern) — extended to the full row
 * width rather than just the icon square.
 *
 * `disabled` is kept as a real, working prop (per §3/§8: "kept specified
 * here, unused, in case a future destination is added to the rail ahead of
 * its screen being built") even though no rail item ships disabled in this
 * build — still tab-reachable, `aria-disabled`, inert on click.
 */
export function RailItem({
  label,
  icon: Icon,
  active,
  disabled = false,
  onSelect,
}: {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly active: boolean;
  readonly disabled?: boolean;
  readonly onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  const iconColor = disabled ? 'var(--text-disabled)' : active ? 'var(--hue-primary-vivid)' : 'var(--text-secondary)';
  const textColor = disabled ? 'var(--text-disabled)' : active ? 'var(--text-primary)' : 'var(--text-secondary)';

  return (
    <button
      type="button"
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        minHeight: 'var(--touch-target-min)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: '0 var(--space-3)',
        borderRadius: 'var(--radius-md)',
        // Hover is a distinct, weaker cue than active (§3: "a hover cue,
        // not a selection cue, so it can't be confused with active") —
        // active always wins if both are true at once.
        background: active
          ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, var(--surface-raised))'
          : hovered && !disabled
            ? 'var(--surface-hover)'
            : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
      }}
    >
      <Icon size={20} strokeWidth={2} color={iconColor} aria-hidden="true" style={{ flex: 'none' }} />
      <span style={{ fontSize: 'var(--type-body-size)', fontWeight: active ? 600 : 500, color: textColor, whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </button>
  );
}
